import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild, ElementRef, Output, EventEmitter, OnChanges, OnDestroy
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { DecryptService } from 'src/app/services/decrypt.service';
import { DisplayService } from '../../../services/display.service';
import { LayerService } from '../../../services/layer.service';
import { PDFService } from '../../../services/pdf.service';
import { PlaneAndHeightService } from '../services/plane-and-height.service';
import { QueryOptionService } from '../services/query-option.service';
import { trigger, style, animate, transition, query, stagger } from '@angular/animations';
import * as loadingLogo from "../../../add-layer/LMI-loading-logo"
import { EditFeatureService } from '../services/edit-feature.service';
import { AppText, featureDisplayCommand, FeatureDrawInfo, featureQueryViewPackage, Languages, mapLayer, UserAccess } from 'src/app/interfaces/common';
import VectorSource from 'ol/source/Vector';
import { UserService } from 'src/app/services/user.service';
import { GeoJSON } from 'ol/format';
import { FeatureService } from 'src/app/services/feature.service';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { GeoJSONFeature, GeoJSONGeometry } from 'ol/format/GeoJSON';

interface stationSettings {
  //Key is the ID of the layer.
  [key: number]: {
    disablePDF?: boolean,
    directToFeature?: boolean,
    featureNameKey?: string
  }
}
@Component({
  selector: 'app-show-station-data',
  templateUrl: './show-station-data.component.html',
  styleUrls: ['./show-station-data.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    /*
      Slide in animation. Here the transition is set when element enters view. first argument of query is a CSS selector
      of the elements that we want to animate/stagger in. Stagger is for applying the animation with a delay for each item
      in the array. So there is a *ngFor loop and inside it .singleRow elements are created, the animation will be applied
      with a 150ms delay for each element that is added to the view.
      The first style is the beginning state of the animation, while the second style is the end of the animation.
      Note: Once the animation is finished none of these 2 styles are applied to the element, instead it is just styled
      as if this animation was not present.
    */
    trigger("slideIn", [
      transition(":enter", [
        query(":enter", [
          //Setting initial style
          style({transform: "translateX(-100%)", opacity: "0"}),
          stagger(20, [
            animate(`0.13s ease-out`, style({transform: "translateX(0%)", opacity: "1"})) //0.2s
          ])
        ], {optional: true})
      ])
    ])
  ]
})

export class ShowStationDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() stationDataFeature: featureQueryViewPackage | null = null;
  @Input() selText: AppText = {};
  @Input() lang: Languages = Languages.Icelandic;
  @Input() applicationSettings: any;
  @Output() closeShowStationViewEE: EventEmitter<string> = new EventEmitter()

  UserAccess = UserAccess;
  stationViewContainer: HTMLElement | null = null;
  //stationLayers contains the layers of most recent feature request where th layer has the query view "station-view"
  stationLayers: mapLayer[] = [];
  rawPropData: { [key: string]: { header: string; props: any[] } } = {};
  readyPropData: { [key: string]: { header: string, features: any[], props: any[] } } = {};
  inFeaturesPicker: boolean = false;
  inLayerPicker: boolean = true;
  navigationObj: { [key: string]: { header: string; features: any[] } } = {};
  featuresToChoose: any[] = [];
  activeStation: any[] = [];
  userAccess = UserAccess.None;
  loginName: string = "";

  /*
    featureGeometry contains the geometries of the features and CRS they are in. They keys are the feature ID.
    Note: You can get the featureGeometry of the current feature the user is viewing by using this.activeStationID as the key into geometry object.
  */
  featureGeometry: { [key: string]: FeatureDrawInfo } = {};
  activeStationID: string | null = null;
  activeLayer: number | null = null;
  selectedDatum: string = "Any";
  selectedCoordsFormat: string = "Lat/Long";
  previousSelectedCoordsFormat: string = "";
  layersAvailableToPick: {header: string, id: number}[] = [];
  //Plane layers that have the ability to fetch more information
  planeLayersWithMoreInfo: string[] = ["PlaneStation", "Grunnkerfi:planestation_isn93", "Grunnkerfi:planestation_isn2004", "Grunnkerfi:planestation_isn2016"];
  datumOptions: string[] = ["Any", "ISN93", "ISN2004", "ISN2016"];
  currentDatumOptions: string[] = this.datumOptions; 
  //Just a boolean indicating if any of the current stationLayers are one fo the layers in planeLayersWithMoreInfo
  isPlaneLayerActive: boolean = false;
  //Gets updated each time a station/feature is shown. Controls if user can edit this feature
  isEditOptionAvailable: boolean = false;
  isUserLoggedIn: boolean = false;
  updateHistoryDateColumn: string = "";
  updateHistoryTypeColumn: string = "";
  newHistoryValueColumn: string = "";

  /*
    Is necessary for edit-feature-data. Keeps a list of all features currently in the table.
    edit-feature-data needs the raw non-mutated feature, but not the feature after it has been
    processed by show-feature-data.component and query-option.service.
  */
  allFeaturesCache: GeoJSONFeature[] = [];
  userConfigAccess: any;
  isInEditMode: boolean = false;
  isHistoryDropdownVisible: boolean = false;
  featureHistoryData: any[] = [];
  currentGmlId: string = "";
  isInEditModeSubscription: Subscription | null = null;
  historySubscription: Subscription | null = null;
  stationInsertedSubscription: Subscription | null = null;
  publishColumn: string = "";
  creatorColumn: string = "";
  editOptions: any[] | null = null; // Contains the options (if any) to be shown in the "Go to edit options" button dropdown
  private rawHeightFeature: any = null;  // Raw GeoJSON feature from getPlaneAndHeight — used for height record editing
  private rawPlaneFeature: any = null;   // Raw GeoJSON feature from getPlaneAndHeight — used for plane record editing
  stationSettings: stationSettings = {};  // Contains station setting per layer

  // Determines if slideIn animation is applied per layer. Key is the layer id, value is either true or false
  slideInAnimationDisabled: { [key: string]: boolean } = {};

  @ViewChild("loadingLogoContainer") loadingLogoContainer: ElementRef | null = null;

  constructor(
    private layerService: LayerService,
    public displayService: DisplayService,
    public editFeatureService: EditFeatureService,
    private userService: UserService,
    private queryOptionService: QueryOptionService,
    private featureService: FeatureService,
    private getPlaneAndHeightService: PlaneAndHeightService,
    private pdfService: PDFService,
    private decryptService: DecryptService,
    private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.editFeatureService.showStationOpen = true;
    this.userConfigAccess = this.applicationSettings.option?.edit?.userConfigAccess;
    this.updateHistoryDateColumn = this.applicationSettings.option?.edit?.history?.updateDateColumn;
    this.updateHistoryTypeColumn = this.applicationSettings.option?.edit?.actionColumn;
    this.newHistoryValueColumn = this.applicationSettings.option?.edit?.history?.newValueColumn;
    this.publishColumn = this.applicationSettings?.option?.edit?.publicationColumn;
    this.creatorColumn = this.applicationSettings?.option?.edit?.creatorColumn;
    this.userAccess = this.userService.getUserAccess();
    this.loginName = this.loginName = this.userService.getUsername();

    if (this.userService.getIsUserLoggedIn().getValue() === true) this.isUserLoggedIn = true;

    this.isInEditModeSubscription = this.displayService.getEditFeatureActive().subscribe((isEditActive: boolean) => {
      this.isInEditMode = isEditActive;
      this.changeDetector.markForCheck();
    })

    this.stationInsertedSubscription = this.editFeatureService.stationInserted$.subscribe(() => {
      const station_nr: string | undefined = this.activeStation.find((row: any) => row.key === 'station_nr')?.value;
      if (!station_nr) return;
      this.removeOldPlanAndHeight();

      // Truncate activeStation at the first height/plane section header
      const firstSectionIdx = this.activeStation.findIndex((row: any) => row.section === 'height' || row.section === 'plane');
      if (firstSectionIdx !== -1) this.activeStation.splice(firstSectionIdx);

      this.getPlaneAndHeight(station_nr);
    });

    if (this.activeStationID != this.editFeatureService.lastStationHistoryFeatureID) this.editFeatureService.lastStationHistoryFeatureID = null;

    //Gets executed once all feature requests have responded from click on the map
    this.featureService.getAllFeatureResponsesArrived().subscribe(() => {
      if (this.stationLayers.length === 1) {
        const layerID = Object.keys(this.navigationObj)[0];
        this.activeLayer = parseInt(layerID)
        this.layerPicked(this.activeLayer)
        this.checkPlaneLayerActive()
        const settingsForSoleLayer = this.stationSettings[this.activeLayer]
        // If true, immideatly show the feature
        if ((settingsForSoleLayer?.directToFeature || this.featuresToChoose.length == 1) && !this.isPlaneLayerActive) {
          this.inFeaturesPicker = false;
          this.inLayerPicker = false;
          this.stationPicked(this.featuresToChoose[0].id)
        }
        else {  // There is only 1 layer, but more than 1 features case here
          this.inLayerPicker = false;
          this.inFeaturesPicker = true;
        }
      }
      else {  // There are more than 1 layers case here
        this.inLayerPicker = true;
        this.inFeaturesPicker = false;
        this.displayService.clearFeaturesByCameFrom("click")
      }
      this.changeDetector.markForCheck()
    })
  }

  ngOnChanges(): void {
    // stationDataFeature was not a valid featureQueryViewPackage
    if (!this.stationDataFeature || !this.stationDataFeature?.geoJSONFeature) return;
    
    const associatedLayer: mapLayer = this.stationDataFeature?.metadataLayer;

    /*
      Detect a refresh: the same layer's data is being updated while the component stays alive (e.g. after saving an edit or insert from station-view).
      Clean up stale data so the subsequent push does not produce duplicates.
    */
    const isRefresh = this.stationLayers.some(l => l.id === associatedLayer.id);
    if (isRefresh) {
      const staleFeatureIDs = new Set((this.rawPropData[associatedLayer.id]?.props || []).map((p: any) => p.featureID).filter(Boolean));
      this.allFeaturesCache = this.allFeaturesCache.filter((f: any) => !staleFeatureIDs.has(f.id));
      this.stationLayers = this.stationLayers.filter(l => l.id !== associatedLayer.id);
    }
   
    //For some reason with the [@.disabled] directive of Angular animations undefined = truthy value? So we put in false if this is the case
    this.slideInAnimationDisabled[associatedLayer.id] = associatedLayer?.query_option?.station_settings?.slideInAnimationDisabled ?? false
    
    if (this.stationDataFeature?.geoJSONFeature?.features && this.stationDataFeature.geoJSONFeature.features.length > 0) {
      //Clone because wee need the feature before it is processed
      this.allFeaturesCache = [...this.allFeaturesCache, ...structuredClone(this.stationDataFeature?.geoJSONFeature?.features)]
    }

    //Once we have reached here we can be sure that there is at least 1 layer with station-view and has more than 0 features in it
    //this.stationViewContainer.style.display = "flex";
    
    //Top level (Layer picker)
    this.rawPropData[associatedLayer.id] = {header: "", props: []};
    //Push relevant layer to stationLayers
    this.stationLayers.push(associatedLayer)
    
    /*
      Special edge case fix: Since features come asynchronously into this component, it might think there is only 1 layer with
      only 1 feature, then it shows it directly and starts panning to it. But we dont want this if there comes feature response
      from another layer into the view at a later time. By calling setRotation() it will cancel any previous command to animate the view
      in any way.
    */

    //Populating stationSettings:
    this.stationSettings[associatedLayer.id] = associatedLayer?.query_option?.station_settings

    for (let v = 0; v < (this.stationDataFeature?.geoJSONFeature?.features?.length ?? 0); v++) {
      const currentStation: string = this.stationDataFeature?.geoJSONFeature?.features[v]?.properties?.station_nr
      // Making sure we don't add duplicates to stations! (This can happen because plane and heigth are 2 layers, but some stations are in both layers which means we get duplicates in stationsToChoose)
      if (associatedLayer.name === "PlaneStation" || associatedLayer.name === "HeightStation") {
        if (this.rawPropData[associatedLayer.id].props.some(alreadyAddedStations => alreadyAddedStations.station_nr === currentStation)) continue;
      }
      
      const featureCRS: string | undefined = this.stationDataFeature?.geoJSONFeature?.crs?.properties?.name
      const geometry: GeoJSONGeometry = this.stationDataFeature?.geoJSONFeature.features[v].geometry
      const id: string | number | undefined = this.stationDataFeature?.geoJSONFeature?.features[v].id;
      if (id) this.featureGeometry[id] = {geometry: geometry, featureCRS: featureCRS ?? ""};

      const props = this.stationDataFeature?.geoJSONFeature?.features?.[v]?.properties
      if (props) props["featureID"] = id;
      
      
      this.rawPropData[associatedLayer.id].header = associatedLayer.title_lang[this.lang]
      this.rawPropData[associatedLayer.id].props.push(this.stationDataFeature?.geoJSONFeature?.features[v].properties)
    }
  
    this.formatData()

    //Populating featuresToChoose
    interface featuresToChooseData {
      header?: string,
      value?: string,
      key?: string,
      id?: string
    }

    let featuresToInsert: featuresToChooseData = {}
    
    //Top level layer picker
    const layersToPick: number[] = Object.keys(this.readyPropData).map(layerID => parseInt(layerID, 10));

    //adding layers and feature in those layers to navigation object.
    layersToPick.forEach((layerID: number) => {
      //Adding to navigationObj the list of layers user can pick from
      this.navigationObj[layerID] = {header: this.readyPropData[layerID].header, features: []}
      
      //Adding to navigationObj the features the user can pick from
      this.readyPropData[layerID].props.forEach(feat => {
        /*
          feat is a list of all properties for a single feature. You can specify in station settings which
          column/property should be the name of the feature in the features list to pick from. Try to find it
          in the array of properties in feat. If not found, the first property is set as the name in the feature list.
        */
        featuresToInsert = feat[0];
        const featureNameKey = associatedLayer?.query_option?.headerToUseInSelection ?? this.stationSettings?.[layerID]?.featureNameKey;
        if (featureNameKey) {
          const foundFeature = feat.find((f: any) => f?.key === featureNameKey);
          if (foundFeature) featuresToInsert = foundFeature;
        }

        /*
          There is on element in feat which is like this: {id: "someFeatureID", hide: true}, take the id from there
          and add it to the element that represents the header-featureName of this feature. (Used also in PDFService as
          the name of the PDF.) Note: feature ID originally comes from "stationID" -> See line 242. Then its parsed/changed in query-option.service
        */
        const id = feat.find( (row: any) => row?.id !== undefined);
        featuresToInsert.id ??= id?.id;
        this.navigationObj[layerID].features.push(featuresToInsert);
      });
    
    })
    this.layersAvailableToPick = this.getAvailableLayers();

    /*
      After a refresh, re-navigate to the same station so the feature view stays visible with the updated data instead of reverting to the layer/feature pickers.
      Exception: if a brand-new feature was created from the station view, its ID differs from activeStationID — navigate to the new feature instead.
    */
    if (isRefresh && this.activeStationID) {
      this.layerPicked(associatedLayer.id);
      const incomingFeatureId = this.stationDataFeature?.geoJSONFeature?.features?.[0]?.id as string | undefined;
      const targetId = (incomingFeatureId && incomingFeatureId !== this.activeStationID) ? incomingFeatureId : this.activeStationID;
      this.stationPicked(targetId);
    }
  }

  ngOnDestroy(): void {
    this.editFeatureService.showStationOpen = false;
    this.isInEditModeSubscription?.unsubscribe();
    this.historySubscription?.unsubscribe();
    this.stationInsertedSubscription?.unsubscribe();
    this.displayService.clearFeaturesByCameFrom("click");
    this.editFeatureService.selectInteraction?.setActive(true);
  }

  //Formats the station data
  formatData(): void {
    for (const layerID in this.rawPropData) {
      const layer: mapLayer | undefined = this.stationLayers.find(l => parseInt(layerID) === l.id);
      if (layer === undefined) {
        console.warn("undefined layer");
        continue;
      }
      const skipColumns: string[] = layer.query_option?.skip_columns || [];
      const renameColumns: object = layer.query_option?.rename_columns || {};
      const unitsOfMeasurement: object = layer.query_option?.measurement_units || {};
      const valueColumns: object = layer.query_option.values_columns || {};

      this.readyPropData[layerID] = { header: this.rawPropData[layerID].header, features: [], props: [] };

      for (let v = 0; v < this.rawPropData[layerID].props.length; v++) {
        let oneStation: any = this.rawPropData[layerID].props[v];
        const columnsWithNoValue: string[] = this.queryOptionService.detectEmptyColumns(oneStation);
        oneStation = this.queryOptionService.removeSkipColumns(skipColumns, oneStation);

        // Always remove empty columns unless keepEmptyColumns is true
        if (!layer?.query_option?.keepEmptyColumns) {
          oneStation = this.queryOptionService.removeEmptyColumns(columnsWithNoValue, oneStation);
        }

        oneStation = this.queryOptionService.applyValueColumns(valueColumns, oneStation);
        oneStation = this.queryOptionService.renameColumns(renameColumns, oneStation, this.lang);
        oneStation = this.queryOptionService.addMeasurementUnits(unitsOfMeasurement, oneStation);

        // Special handling for plane layers
        const activeLayerName: string | undefined = layer.name;
        if (activeLayerName && this.planeLayersWithMoreInfo.includes(activeLayerName)) {
          const pathRowIndex: number = oneStation.findIndex((row: any) => row.key === "path");
          if (pathRowIndex !== -1) {
            oneStation.splice(pathRowIndex + 1, 0, { header: this.selText.approxPosition, value: "", section: "station" });
          }
        }
        this.readyPropData[layerID].props.push(oneStation);
      }
    }
  }

  closeView(): void {
    this.editFeatureService.clearInteractions(true, true);
    this.editFeatureService.hideVertices();
    this.closeShowStationViewEE.emit("")
  }

  layerPicked(pickedLayer: number): void {
    this.inLayerPicker = false;
    this.inFeaturesPicker = true;
    // When the layer-id comes back through the template it might have been turned into a string, if so convert back to number
    this.activeLayer = typeof(pickedLayer) === "string" ? parseInt(pickedLayer) : pickedLayer;
    this.editOptions = this.goToEditOptions();
    this.featuresToChoose = this.navigationObj[pickedLayer].features;
    this.setDatumOptions();
    this.checkPlaneLayerActive()
  }

  //Gets executed when user chooses a station...
  stationPicked(pickedStation: string, event?: PointerEvent): void {
    this.setActiveStation(pickedStation);
    //Setting activeStationID to featureID of a single feature that came from feature request response 
    this.activeStationID = pickedStation;
    this.setEditOption(this.activeStationID)

    //Only convert if we are dealing with station layer info
    if (this.areStationLayersActive()) {
      //Always send station_nr as the value to the getPlaneAndHeight HTTP call.    
      const station_nr: string = this.activeStation.find( (row) => row.key === "station_nr")?.value
      this.getPlaneAndHeight(station_nr)
      this.convertToDMS()
    }
    if (event && this.editFeatureService.selectInteraction && this.isEditOptionAvailable) this.setFeatureToSelect(pickedStation);

    //Draw feature when its picked, if settings are such
    if (this.activeStationID && this.applicationSettings?.option?.featureOnHover?.draw !== false) {
      //If event is undefined the call to stationPicked whas made programmatically, this is only done when there is only 1 feature we want to show immediately.
      //No panning if feature is bigger than the viewport - This is determined inside of displayFeature()
      if (!event) {
        this.displayFeature(this.stationLayers[0].id, "click", {draw: null, pan: false, zoom: null})
        return
      }
      this.displayFeature(this.stationLayers[0].id, "click")
    }
  }

  setFeatureToSelect(featureId: string): void {
    const currentLayer = this.editFeatureService.mapLayers.find(layer => layer.name === featureId.split('-')[0]);
    const source = currentLayer?.layer?.getSource() as VectorSource;
    if (source) {
      const features = source?.getFeatures();
      const desiredFeatre = features.find(feature => feature.getId() === featureId);
      if (desiredFeatre) {
        this.editFeatureService.selectInteraction?.getFeatures().clear();
        this.editFeatureService.selectInteraction?.getFeatures().push(desiredFeatre);
      }
    }
  }

  setEditOption(featureID: string): void {
    if (this.editFeatureService.areaPolygon && this.stationDataFeature?.geoJSONFeature?.features) {
      const feature = this.stationDataFeature.geoJSONFeature.features.find((geoFeature: any) => geoFeature.id === featureID);

      if (feature) {
        const format = new GeoJSON();
        const olFeature = format.readFeature(feature) as Feature<Geometry>;
        const isWithinOrIntersectingArea: boolean | null = this.featureService.isFeatureAWithinOrIntersectingFeatureB(olFeature, this.editFeatureService.areaPolygon);

        this.isEditOptionAvailable = isWithinOrIntersectingArea ?? false;
        return;
      }
    }

    // If the feature is not found or areaPolygon is not available
    const associatedLayer: mapLayer | undefined = this.layerService.findLayerByFeatureId(featureID);
    if (associatedLayer?.option?.mp_layer?.edit?.editable) this.isEditOptionAvailable = true;
    else this.isEditOptionAvailable = false;
  }

  setActiveStation(pickedStation: string): void {
    let findStation: any[] = [];
  
    if (this.activeLayer !== null && this.readyPropData[this.activeLayer] !== null) {
      const props = this.readyPropData[this.activeLayer].props;
      if (Array.isArray(props)) {
        for (let i = 0; i < props.length; i++) {
          const feat = props[i];
          feat.forEach((row: any) => {
            if (row.id === pickedStation) {
              findStation = props[i];
              return;
            }
          });
        }
      }
    }
    this.activeStation = findStation;
    this.inFeaturesPicker = false;
  }
  
  goBackToPickers(): void {
    this.editFeatureService.clearInteractions(true, false);
    this.isHistoryDropdownVisible = false;
    
    this.previousSelectedCoordsFormat = this.selectedCoordsFormat;
    //if both are false -> user is coming from the feature view -> push them up to feature picker view
    if (this.inLayerPicker == false && this.inFeaturesPicker == false) {
      this.removeOldPlanAndHeight();
      this.inLayerPicker = false;
      this.inFeaturesPicker = true;
      this.displayService.clearFeaturesByCameFrom("click")
      return
    }
    //if inFeaturesPicker is true then uer is in feature picker view -> push them up to layer picker view
    if (this.inFeaturesPicker == true) {
      this.inLayerPicker = true;
      this.inFeaturesPicker = false;
      return;
    }
  }

  getPlaneAndHeight(station: string): void {
    const phInfo: Observable<any> = this.getPlaneAndHeightService.getplaneAndHeight(station);
  
    phInfo.subscribe((encryptedData: any) => {
      if (!("lets_start_the_show" in encryptedData)) return;
  
      const decryptedData: any = this.decryptService.decryptCF(encryptedData?.lets_start_the_show);
      const heightDataExists: number = decryptedData?.result?.height?.features?.length;
      const planeDataExists: number = decryptedData?.result?.plane?.features?.length;
  
      if (heightDataExists !== undefined && heightDataExists !== 0) {
        // Clone before formatHeightProps, because removeSkipColumns mutates .properties in place
        this.rawHeightFeature = structuredClone(decryptedData?.result?.height?.features?.at(-1)) ?? null;
        this.formatHeightProps(decryptedData?.result?.height?.features.at(-1)?.properties);
      }

      if (
        planeDataExists !== undefined &&
        planeDataExists !== 0 &&
        this.activeLayer !== null &&
        this.stationLayers.find(l => l.id == this.activeLayer)?.name !== "HeightStation"
      )
      {
        // Clone before formatPlaneProps, because removeSkipColumns mutates .properties in place
        this.rawPlaneFeature = structuredClone(decryptedData?.result?.plane?.features?.at(-1)) ?? null;
        if (this.selectedDatum !== "Any") {
          const datumRow: object | null = this.checkRequestedDatumAvailable(decryptedData?.result?.plane?.features);
          if (datumRow === null) this.formatPlaneProps(decryptedData?.result?.plane?.features.at(-1)?.properties, false);
          else {
            const matchedFeature = decryptedData?.result?.plane?.features?.find(
              (f: any) => f.properties?.proj_datum === this.selectedDatum
            );
            if (matchedFeature) this.rawPlaneFeature = structuredClone(matchedFeature);
            this.formatPlaneProps(datumRow, true);
          }
        }
        else this.formatPlaneProps(decryptedData?.result?.plane?.features.at(-1)?.properties, true);
      }
      this.editOptions = this.goToEditOptions();
      this.changeDetector.markForCheck();
    });
  }

  // Return values: If requested datum was found: then return the properties of that row. If not found: return null.
  checkRequestedDatumAvailable(planeRows: any[]): object | null {
    for (let i = 0; i < planeRows.length; i++) {
      const row = planeRows[i]
      if (this.selectedDatum === row.properties.proj_datum) return row.properties;
    }
    return null;
  }

  /*
    Should accept height info from "gr"(get records) -> remove skipColumns -> apply renameColumns -> apply units of measurement -> Add with an header to the displayed UI
  */
  formatHeightProps(heightProps: any): void {
    const heightLayer: mapLayer | undefined = this.getLayer("HeightStation") ?? this.editFeatureService.mapLayers.find(l => l.name === "HeightStation");
    if (heightLayer === undefined) return;

    const heightSkipColumns: string[] = heightLayer?.query_option?.height_skip_columns;
    const heightRenameColumns: object[] = heightLayer?.query_option?.height_rename_columns;
    const unitsOfMeasurement: object = heightLayer?.query_option?.height_measurements_units;

    heightProps = this.queryOptionService.removeSkipColumns(heightSkipColumns, heightProps)
    heightProps = this.queryOptionService.renameColumns(heightRenameColumns, heightProps, this.lang)
    heightProps = this.queryOptionService.addMeasurementUnits(unitsOfMeasurement, heightProps)

    //Changing accuracy of height in heightProps to be 3 decimal places
    heightProps.forEach((row: any) => {
      if (row.key === "height") {
        let heightValue: string = row.value
        heightValue = parseFloat(heightValue.substring(0, heightValue.length - 2)).toFixed(3)
        row.value = heightValue + " m"
      }
    });

    //adding a header - The fact that section is defined identifies this as a header
    this.activeStation.push({header: this.selText.heightData, value: "", section: "height"})
    //Finally adding to these props to UI
    heightProps.forEach( (prop: any) => {
      this.activeStation.push(prop);
    });

  }

  /*
    Should accept plane info from "gr"(get records) -> remove skipColumns -> apply renameColumns -> Add with an header to the displayed UI
  */
  formatPlaneProps(planeProps: any, isInRightDatum: boolean): void {
    
    //const planeLayer: mapLayer = this.getLayer(this.activeLayer);
    const planeLayer: mapLayer | undefined = this.getLayer(this.activeLayer ?? ""); // Provide a default value if activeLayer is null
    
    let planeSkipColumns: string[] = planeLayer?.query_option?.plane_skip_columns;
    const planeRenameColumns: object[] = planeLayer?.query_option?.plane_rename_columns;
    const unitsOfMeasurment: object = planeLayer?.query_option?.plane_measurements_units;

    //To remove non-chosen coordinate system -> add them to skip-columns at run time? -> skipOtherCoordsFormat
    //OBS: need to make a shallow copy of planeSkipColumns so that it doesnt continually add skipColumns if user is swizzling same station.
    planeSkipColumns = this.skipOtherCoordsFormat([...planeSkipColumns]);
    planeProps = this.queryOptionService.removeSkipColumns(planeSkipColumns, planeProps);

    //Formating numbers in plane-answer to have only 3 decimal places.
    for (const key in planeProps) {
      const decimalCount: number = this.countDecimals(planeProps[key])
      // (27.April: except lat/long in plane Answer then we want full precision)
      if (key === "lat" || key === "long") continue;

      if (decimalCount > 3) planeProps[key] = planeProps[key].toFixed(3);
    }

    planeProps = this.queryOptionService.renameColumns(planeRenameColumns, planeProps, this.lang);
    planeProps = this.queryOptionService.addMeasurementUnits(unitsOfMeasurment, planeProps);
  
    //Display "coords not available for x datum" Logic here
    if (isInRightDatum == false) {
      this.formatDatumNotAvailable()
      return
    }
    
    //removing possible old "no datum row"
    //this.activeStation.findIndex( row => row?.noDatumRow !== undefined)
    this.activeStation = this.activeStation.filter( row => row?.noDatumRow === undefined )
    
    //adding a plan header - The fact that section is defined identifies this as a header
    this.activeStation.push({header: this.selText.planeData, value: "", section: "plane"})
    //Finally adding to these props to UI
    planeProps.forEach( (prop: any) => {
      this.activeStation.push(prop);
    });
  }

  //Should convert the decimal lat/lon in station section into degrees, minutes, seconds format and replace the decimal values in the view.
  convertToDMS(): void {
    const latIndex = this.activeStation.findIndex( row => row?.stationLatLon !== undefined )
    if (latIndex === -1) return
    //If its a string it means its already converted, in that case just return.
    if (typeof (this.activeStation[latIndex].value) === "string") { return }

    const latLonCoords = this.activeStation.filter( (row) => row?.stationLatLon !== undefined ).map( elem => elem.value).reverse()
    const coordsInDMS = this.displayService.decimal2DMS(latLonCoords).split("  ")

    this.activeStation[latIndex].value = coordsInDMS[0]
    this.activeStation[latIndex + 1].value = coordsInDMS[1]
  }

  //Should take in skipColumns, add to them the columns to skip: if LCC is the picked coords-system, then remove cartesian and Lat/Long
  skipOtherCoordsFormat(skipColumns: string[]): string[] {
    switch(this.selectedCoordsFormat) {
      case "LCC":
        skipColumns.push("cart_x");
        skipColumns.push("cart_y");
        skipColumns.push("cart_z");
        skipColumns.push("lat");
        skipColumns.push("long");
        return skipColumns;
      case "Lat/Long":
        skipColumns.push("cart_x");
        skipColumns.push("cart_y");
        skipColumns.push("cart_z");
        skipColumns.push("east_coord")
        skipColumns.push("north_coord")
        skipColumns.push("scale")
        skipColumns.push("mer_con")
        return skipColumns;
      case "Cartesian":
        skipColumns.push("ellips_height_acc")
        skipColumns.push("ellips_height")
        skipColumns.push("lat");
        skipColumns.push("long");
        skipColumns.push("east_coord")
        skipColumns.push("north_coord")
        skipColumns.push("plane_acc")
        skipColumns.push("scale")
        skipColumns.push("mer_con")
        return skipColumns;
    }
    return skipColumns
  }

  //Should take in readyPropData and somewhere display "These coords format not available..." because datum was not available.
  formatDatumNotAvailable(): void {
    //Checking if there is already a noDatumRow in activeStation, if so just return.
    if (this.activeStation.some( row => row?.noDatumRow !== undefined) == true) return

    const i: number = this.activeStation.findIndex( row => row?.coordsInsertionPoint !== undefined)
    this.activeStation.splice(i+1, 0, {header: "Info", value: this.selText.noCoordsForThisDatum, noDatumRow: true})

  }

  //The coordinates are received in(plane) the answer to getPlaneAndHeight. I need to move these from plane table and up to 
  //the station section in the view. And possibly remove the existing Lat/Long or other coords already in the station section.
  moveCoordsToStation(): void {
    const i: number = this.activeStation.findIndex( row => row?.coordsInsertionPoint !== undefined)
    const amountToRemove: number = this.previousSelectedCoordsFormat !== "Cartesian" ? 2 : 3;
    //Checking if there are already coordinates in the station section, if so remove them because they are old.
    if (this.activeStation[i+1]?.canMove == true) {
      this.activeStation.splice(i+1, amountToRemove);
    }

    const coords: object[] = this.activeStation.filter( (row) => row?.canMove !== undefined);

    //Splice can insert into array aswell at index x! ... is a destructuring assignment, eg: frees all elements from the coords array.
    this.activeStation.splice(i+1, 0, ...coords);
  }

  generatePDF(): void {
    this.pdfService.addSeltext(this.selText);
    let geometryToPickedStation: any;

    if (this.activeStationID) geometryToPickedStation = this.featureGeometry?.[this.activeStationID]?.geometry;
    
    // Start the loading animation
    const logo: HTMLCanvasElement = loadingLogo.getLoadingLogo(600, 600);
    logo.classList.add("loadingLogo");
    loadingLogo.startAnimation();
    this.loadingLogoContainer?.nativeElement.appendChild(logo);
  
    let layer: mapLayer | undefined;
    let HTMLColumns: string[] = [];
    
    if (this.activeLayer !== null) {
      layer = this.layerService.findLayerByID(this.activeLayer);
      HTMLColumns = layer?.query_option?.html_columns ?? [];
    }
    
    this.pdfService.getFeaturePDF(this.activeStation, this.activeStation.find( row => row?.id !== undefined )?.value ?? this.activeStation[0]?.value, geometryToPickedStation, HTMLColumns)
      .subscribe(() => {
        // End the loading animation
        loadingLogo.pauseAnimation();
        logo.remove();
      });
  }  

  //Why? This functions exists for removing old plan and height info, usually this is removed automatically, but if user
  //goes into a station -> then out again -> then into same station = that station has double plane and height info.
  removeOldPlanAndHeight(): void {
    if (this.activeLayer !== null && this.readyPropData[this.activeLayer]?.props) {
      for (let i = 0; i < this.readyPropData[this.activeLayer].props.length; i++) {
        for (let v = 0; v < this.readyPropData[this.activeLayer].props[i].length; v++) {
          if (
            this.readyPropData[this.activeLayer].props[i][v]?.section === "plane" ||
            this.readyPropData[this.activeLayer].props[i][v]?.section === "height"
          ) {
            this.readyPropData[this.activeLayer].props[i].splice(v);
            break;
          }
        }
      }
    }
  }  

  //Just little utility function to pick between height layer or the 4 plane layers (planeStation(all) | ISN93 layer | ISN2004 layer | ISN2016 layer)
  getLayer(layerID: number | string): mapLayer | undefined {
    //If its a string, then its the name of the layer
    if (typeof(layerID) === "string") {
      return this.stationLayers.find(l => l.name === layerID);
    }
    //if its number, then its the id in the layer
    return this.stationLayers.find(l => l.id === layerID);
  }

  //I am checking here if any layer has the "feature" of getting additional information from server
  areStationLayersActive(): boolean {
    const activeLayer = this.stationLayers.find( l => l.id == this.activeLayer);
    if (activeLayer === undefined) return false;
    if (this.planeLayersWithMoreInfo.includes(activeLayer.name) || activeLayer.name === "HeightStation") return true;
    else return false;
  }

  /* Should count decimal places in a number */
  countDecimals(numb: number): number {
    if (typeof(numb) !== "number") return 0
    const arr = numb.toString().split(".");
    if (arr.length < 2) return 0
    return arr[1].length
  }

  checkPlaneLayerActive(): void {
    const activeLayer = this.stationLayers.find( l => l.id == this.activeLayer)
    if (activeLayer === undefined) {
      this.isPlaneLayerActive = false;
      return;
    }
    if (this.planeLayersWithMoreInfo.includes(activeLayer.name)) {
      this.isPlaneLayerActive = true;
      return;
    } 
    this.isPlaneLayerActive = false;  // Is this needed?
  }

  //these are layers that are in navigationObj which are layers that are active since last click on map
  getAvailableLayers(): {header: string, id: number}[] {
    const layers: {header: string, id: number}[] = []
    for (const key in this.navigationObj) {
      layers.push({header: this.navigationObj[key].header, id: +key})
    }
    return layers;
  }

  //Should constrict what options are available in the selection of datums.
  setDatumOptions(): void {
    if(this.activeLayer) {
      const currentLayer = this.getLayer(this.activeLayer);
      if (currentLayer === undefined) return;
  
      if (currentLayer.name === "PlaneStation") {
        this.currentDatumOptions = this.datumOptions
        this.selectedDatum = "Any"
      }
  
      if (currentLayer.name === "Grunnkerfi:planestation_isn93") {
        this.currentDatumOptions = ["ISN93"]
        this.selectedDatum = "ISN93"
      }
  
      if (currentLayer.name === "Grunnkerfi:planestation_isn2004") {
        this.currentDatumOptions = ["ISN2004"]
        this.selectedDatum = "ISN2004"
      }
  
      if (currentLayer.name === "Grunnkerfi:planestation_isn2016") {
        this.currentDatumOptions = ["ISN2016"]
        this.selectedDatum = "ISN2016"
      }
    }
  }

  displayFeature(layerID: number, cameFrom: "click" | "search", fdc?: featureDisplayCommand): void {
    if (!this.activeStationID) {
      console.warn("activeStationID is not valid. Cannot display the feature. activeStationID was: " + this.activeStationID)
      return
    }

    const featDrawInfo: FeatureDrawInfo = this.featureGeometry?.[this.activeStationID]

    if (!featDrawInfo?.geometry) {
      console.warn("No valid geometry for this feature. Cannot draw")
      return
    }

    const shouldPanToFeature: boolean | null = this.featureService.disablePanIfDisorientating(featDrawInfo)
    if (!shouldPanToFeature) {
      fdc = {...fdc, ...{pan: false}}
    }
    
    this.displayService.displayFeature(featDrawInfo, layerID, cameFrom, fdc)
  }
  
  goToEditFeature(layerName?: string): void {
    this.editFeatureService.initializeLength();

    let layerFeatCameFrom: mapLayer | undefined;
    if (layerName) layerFeatCameFrom = this.layerService.findLayerByName(layerName);
    else if (this.activeLayer !== null) layerFeatCameFrom = this.layerService.findLayerByID(this.activeLayer);
  
    if (!layerFeatCameFrom) {
      console.warn("Layer not found! Cannot proceed with editing feature.");
      return;
    }
  
    this.editFeatureService.setCurrentLayer(layerFeatCameFrom);
    this.displayService.setEditFeatureActive(true);
  
    const featureToEdit: GeoJSONFeature | undefined = this.allFeaturesCache.find((feature: any) => feature.id === this.activeStationID);
  
    if (!featureToEdit) {
      console.warn("Feature not found! Can't set into editFeatureService");
      return;
    }
  
    this.editFeatureService.setNewGeoFeature(featureToEdit);

    if(layerFeatCameFrom?.option?.mp_layer?.edit?.geoEditable === true) {
      // To keep components still open when 'click' event is captured in map component
      this.displayService.disallowFeatureClick();
      this.editFeatureService.addModifyInteraction(featureToEdit);
    }
    this.editFeatureService.selectInteraction?.setActive(false);
    if (layerFeatCameFrom?.option?.mp_layer?.edit?.startSnap !== false) {
      this.editFeatureService.setSnapToFeaturesAndRestrictedAreas(); // Add Snaps to areas/features if needed
      this.editFeatureService.setSnapToDragAndDropFeatures();
    }
    else this.editFeatureService.removeAllSnaps();
  }

  goToEditOptions(): any[] | null {
    const hasHeightData = this.activeStation.some((row: any) => row.section === 'height');
    if (hasHeightData) {
      const heightLayer = this.editFeatureService.mapLayers.find(l => l.name === 'HeightStation');
      const heightOptions: any[] = heightLayer?.option?.mp_layer?.edit?.goToEditOptions ?? [];
      return heightOptions.length ? heightOptions : null;
    }

    const hasPlaneData = this.activeStation.some((row: any) => row.section === 'plane');
    if (hasPlaneData) {
      const planeLayer = this.editFeatureService.mapLayers.find(l => l.name === 'PlaneStation');
      const planeOptions: any[] = planeLayer?.option?.mp_layer?.edit?.goToEditOptions ?? [];
      return planeOptions.length ? planeOptions : null;
    }

    const layer = this.activeLayer !== null ? this.layerService.findLayerByID(this.activeLayer) : undefined;
    const options = layer?.option?.mp_layer?.edit?.goToEditOptions || null;
    return options;
  }

  onEditOptionSelected(option: any): void {
    // Store per-action refreshLayers so refreshDependentLayers() can use them instead of the app-level list
    this.editFeatureService.activeEditOptionRefreshLayers = option.refreshLayers?.length ? option.refreshLayers : null;

    if (option.action === 'update') {
      /*
        Track the layer the user was actually viewing so it can be refreshed after the update
        (currentLayer will be set to the option's layerName, which may differ from the viewed layer)
      */
      const activeMapLayer = this.activeLayer !== null ? this.layerService.findLayerByID(this.activeLayer) : null;
      this.editFeatureService.updateSourceLayer = activeMapLayer?.layer ? activeMapLayer : (this.stationLayers.find(l => !!l.layer) ?? null);
      if (option.table === 'grunnkerfi.height') this.goToEditFeatureFromRaw(option.layerName, this.rawHeightFeature, option.table);
      else if (option.table === 'grunnkerfi.plane') this.goToEditFeatureFromRaw(option.layerName, this.rawPlaneFeature, option.table);
      else this.goToEditFeature(option.layerName);
    }
    else if (option.action === 'insert') this.goToInsertFeature(option);
  }

  goToEditFeatureFromRaw(layerName: string, rawFeature: any, tableName?: string): void {
    if (!rawFeature) {
      console.warn('No height/plane data available to edit for this option');
      return;
    }
    this.editFeatureService.initializeLength();
    const layerFeatCameFrom = this.layerService.findLayerByName(layerName);
    if (!layerFeatCameFrom) {
      console.warn('Layer not found! Cannot proceed with editing feature.');
      return;
    }

    /*
    If a tableName override is provided, create a proxy layer with that name so edit-feature-data's initializeVariables
    looks up the right board config (e.g. tables['grunnkerfi.plane'] instead of tables['PlaneStation']).
    */
    const layerForLookup: mapLayer = tableName ? { ...layerFeatCameFrom, name: tableName } : layerFeatCameFrom;
    this.editFeatureService.setCurrentLayer(layerForLookup);
    this.displayService.setEditFeatureActive(true);
    // Height/plane records are non-spatial — their geometry is null. Substitute the station geometry so setNewGeoFeature doesn't crash.
    const stationGeom = this.activeStationID ? (this.featureGeometry?.[this.activeStationID]?.geometry ?? { type: '', coordinates: [] }) : { type: '', coordinates: [] };
    const featureToEdit = rawFeature.geometry ? rawFeature : { ...rawFeature, geometry: stationGeom };
    this.editFeatureService.setNewGeoFeature(featureToEdit);
    this.editFeatureService.selectInteraction?.setActive(false);
    if (layerFeatCameFrom?.option?.mp_layer?.edit?.startSnap !== false) {
      this.editFeatureService.setSnapToFeaturesAndRestrictedAreas();
      this.editFeatureService.setSnapToDragAndDropFeatures();
    } else {
      this.editFeatureService.removeAllSnaps();
    }
  }

  goToInsertFeature(option: any): void {
    const tableName = option.table;
    const tableConfig = this.applicationSettings?.option?.tables?.[tableName];

    // Save station context so the service can refresh the station-view after the insert
    this.editFeatureService.stationFeatureID = this.activeStationID ?? '';
    this.editFeatureService.stationLayerName = option.layerName ?? '';

    this.editFeatureService.currentLayer = null;
    this.editFeatureService.featureLayer = tableName;
    this.editFeatureService.featureType = tableConfig?.geometryType || '';

    this.displayService.setEditFeatureActive(true);
    let station_nr = undefined;
    if (this.activeStation && Array.isArray(this.activeStation)) {
      const stationNrRow = this.activeStation.find(row => row.key === 'station_nr');
      station_nr = stationNrRow ? stationNrRow.value : undefined;
    }
    this.editFeatureService.setNewGeoFeature({ properties: { station_nr }, geometry: { type: '', coordinates: [] } });
  }
}